import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSelectField } from '@/components/forms/form-select-field';
import { Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { ImageFileInput } from '@/components/forms/image-file-input';

import { campaignSchema, type CampaignFormValues } from './campaign-schema';

interface CampaignFormProps {
  initialValues?: Partial<CampaignFormValues>;
  onSubmit: (values: CampaignFormValues) => Promise<void>;
  isSubmitting?: boolean;
  onCancel: () => void;
  isEditing?: boolean;
  initialHorizontalMediaUrl?: string;
  initialVerticalMediaUrl?: string;
}

export function CampaignForm({
  initialValues,
  onSubmit,
  isSubmitting,
  onCancel,
  isEditing,
  initialHorizontalMediaUrl,
  initialVerticalMediaUrl,
}: CampaignFormProps) {
  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignSchema),
    defaultValues: {
      type: initialValues?.type || 'DASHBOARD',
      startDateTime: initialValues?.startDateTime || '',
      endDateTime: initialValues?.endDateTime || '',
      horizontalMedia: undefined,
      verticalMedia: undefined,
    },
  });

  const [horizontalPreviewUrl, setHorizontalPreviewUrl] = useState<string | null>(
    initialHorizontalMediaUrl || null
  );
  const [verticalPreviewUrl, setVerticalPreviewUrl] = useState<string | null>(
    initialVerticalMediaUrl || null
  );

  const selectedHorizontalMedia = useWatch({ control: form.control, name: 'horizontalMedia' });
  const selectedVerticalMedia = useWatch({ control: form.control, name: 'verticalMedia' });
  const type = useWatch({ control: form.control, name: 'type' });

  useEffect(() => {
    if (selectedHorizontalMedia && selectedHorizontalMedia instanceof File) {
      const objectUrl = URL.createObjectURL(selectedHorizontalMedia);
      // use setTimeout to avoid synchronous setState inside an effect
      const timeoutId = setTimeout(() => {
        setHorizontalPreviewUrl(objectUrl);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [selectedHorizontalMedia]);

  useEffect(() => {
    if (selectedVerticalMedia && selectedVerticalMedia instanceof File) {
      const objectUrl = URL.createObjectURL(selectedVerticalMedia);
      const timeoutId = setTimeout(() => {
        setVerticalPreviewUrl(objectUrl);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [selectedVerticalMedia]);

  const handleCustomSubmit = (values: CampaignFormValues) => {
    if (!isEditing) {
      if (values.type === 'DASHBOARD' && !values.horizontalMedia) {
        form.setError('horizontalMedia', { message: 'Media is required' });
        return;
      }
      if (values.type === 'POPUP' && !values.horizontalMedia && !values.verticalMedia) {
        form.setError('horizontalMedia', {
          message: 'Either horizontal or vertical media is required',
        });
        form.setError('verticalMedia', {
          message: 'Either horizontal or vertical media is required',
        });
        return;
      }
    } else {
      if (values.type === 'DASHBOARD' && !values.horizontalMedia && !horizontalPreviewUrl) {
        form.setError('horizontalMedia', { message: 'Media is required' });
        return;
      }
      if (
        values.type === 'POPUP' &&
        !values.horizontalMedia &&
        !values.verticalMedia &&
        !horizontalPreviewUrl &&
        !verticalPreviewUrl
      ) {
        form.setError('horizontalMedia', {
          message: 'Either horizontal or vertical media is required',
        });
        form.setError('verticalMedia', {
          message: 'Either horizontal or vertical media is required',
        });
        return;
      }
    }
    onSubmit(values);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleCustomSubmit)} className="flex flex-col">
        <div className="grid gap-6 px-6 py-4">
          <FormSelectField
            control={form.control}
            name="type"
            label="Placement"
            placeholder="Select placement"
            required
            options={[
              { label: 'Dashboard Banner', value: 'DASHBOARD' },
              { label: 'Popup on Community Open', value: 'POPUP' },
            ]}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormInputField
              control={form.control}
              name="startDateTime"
              label="Start Date & Time"
              type="datetime-local"
              inputClassName="block w-full"
            />
            <FormInputField
              control={form.control}
              name="endDateTime"
              label="End Date & Time"
              type="datetime-local"
              inputClassName="block w-full"
            />
          </div>

          <div
            className={`grid ${type === 'POPUP' ? 'grid-cols-1 md:grid-cols-2 gap-6' : 'grid-cols-1'}`}
          >
            {(type === 'DASHBOARD' || type === 'POPUP') && (
              <FormField
                control={form.control}
                name="horizontalMedia"
                render={({ field: { value: _value, onChange, ...field } }) => (
                  <FormItem>
                    <FormLabel>
                      {type === 'POPUP' ? 'Horizontal Media' : 'Media (Image)'}
                      {!isEditing && type === 'DASHBOARD' && (
                        <span className="text-red-500"> *</span>
                      )}
                      {type === 'POPUP' && (
                        <span className="text-muted-foreground font-normal ml-1">
                          (Optional if vertical is provided)
                        </span>
                      )}
                    </FormLabel>
                    <FormControl>
                      <div className="space-y-4">
                        <ImageFileInput
                          className="block pt-2.5"
                          accept="image/*"
                          onPrepared={onChange}
                          name={field.name}
                          onBlur={field.onBlur}
                          ref={field.ref}
                        />
                        {horizontalPreviewUrl && (
                          <div className="relative w-[85vw] sm:w-[320px] md:w-[350px] max-w-full aspect-video overflow-hidden rounded-2xl border border-border/10 shadow-sm bg-muted/30">
                            <img
                              src={horizontalPreviewUrl}
                              alt="Horizontal media preview"
                              className="h-full w-full object-cover block"
                            />
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {type === 'POPUP' && (
              <FormField
                control={form.control}
                name="verticalMedia"
                render={({ field: { value: _value, onChange, ...field } }) => (
                  <FormItem>
                    <FormLabel>
                      Vertical Media
                      <span className="text-muted-foreground font-normal ml-1">
                        (Optional if horizontal is provided)
                      </span>
                    </FormLabel>
                    <FormControl>
                      <div className="space-y-4">
                        <ImageFileInput
                          className="block pt-2.5"
                          accept="image/*"
                          onPrepared={onChange}
                          name={field.name}
                          onBlur={field.onBlur}
                          ref={field.ref}
                        />
                        {verticalPreviewUrl && (
                          <div className="relative aspect-[9/16] w-full max-w-[200px] overflow-hidden rounded-xl border border-border/50 bg-muted/30">
                            <img
                              src={verticalPreviewUrl}
                              alt="Vertical media preview"
                              className="h-full w-full object-contain"
                            />
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>
        <div className="flex flex-col-reverse justify-end gap-3 border-t border-border px-6 py-6 md:flex-row">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="w-full text-muted-foreground hover:bg-muted hover:text-foreground md:w-auto"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary px-8 text-white shadow-lg shadow-primary/20 hover:bg-primary/90 md:w-auto"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {isEditing ? 'Save Campaign' : 'Create Campaign'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
